import type { ScenarioStepTimingV1 } from 'api/scenarios';

export const MINUTES_PER_HOUR = 60;
export const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
export const MINUTES_PER_DAY_MAX = MINUTES_PER_DAY - 1;
export const UNSET_TIMING_LABEL = '待ち時間が未設定';

export type DurationParts = { days: number; hours: number; minutes: number };
/** 入力途中の空欄をそのまま保持するための表現。 */
export type DurationDraft = { days: string; hours: string; minutes: string };

export function splitMinutes(total: number | null): DurationParts | null {
  if (total === null || !Number.isFinite(total) || total < 0) return null;
  const whole = Math.floor(total);
  return {
    days: Math.floor(whole / MINUTES_PER_DAY),
    hours: Math.floor((whole % MINUTES_PER_DAY) / MINUTES_PER_HOUR),
    minutes: whole % MINUTES_PER_HOUR,
  };
}

export function joinMinutes({ days, hours, minutes }: DurationParts): number {
  return days * MINUTES_PER_DAY + hours * MINUTES_PER_HOUR + minutes;
}

export function toDraft(total: number | null): DurationDraft {
  const parts = splitMinutes(total);
  if (!parts) return { days: '', hours: '', minutes: '' };
  return { days: String(parts.days), hours: String(parts.hours), minutes: String(parts.minutes) };
}

/** 3欄すべてが空なら未設定、1つでも入っていれば空欄を0として合算する。 */
export function fromDraft({ days, hours, minutes }: DurationDraft): number | null {
  const fields = [days, hours, minutes];
  if (fields.every((field) => field.trim() === '')) return null;
  const values = fields.map((field) => (field.trim() === '' ? 0 : Number(field)));
  if (values.some((value) => !Number.isInteger(value) || value < 0)) return null;
  return joinMinutes({ days: values[0], hours: values[1], minutes: values[2] });
}

/** 「1日3時間30分」。0分ちょうどは「0分」。 */
export function formatDuration(total: number | null): string {
  const parts = splitMinutes(total);
  if (!parts) return UNSET_TIMING_LABEL;
  const text = `${parts.days ? `${parts.days}日` : ''}${parts.hours ? `${parts.hours}時間` : ''}${parts.minutes ? `${parts.minutes}分` : ''}`;
  return text || '0分';
}

export function formatMinuteOfDay(minuteOfDay: number | null): string | null {
  if (minuteOfDay === null || !Number.isInteger(minuteOfDay) || minuteOfDay < 0 || minuteOfDay > MINUTES_PER_DAY_MAX) return null;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(Math.floor(minuteOfDay / MINUTES_PER_HOUR))}:${pad(minuteOfDay % MINUTES_PER_HOUR)}`;
}

export function parseMinuteOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * MINUTES_PER_HOUR + minutes;
}

/**
 * 並べ替え・削除で位置が変わったstepのtimingを、その位置で選べる表現へ読み替える。
 * どちらの向きも待機値と実際の配信タイミングを変えない同義変換で、画面の選択肢と保存内容を揃えるためだけに行う。
 * - 先頭のrelativeは前のstepが無いのでelapsedへ（待機値はそのまま）
 * - 2つ目以降のelapsed 0は「登録直後」の選択肢を持たないのでrelative 0へ
 */
export function normalizeTimingForIndex(timing: ScenarioStepTimingV1 | null, index: number): ScenarioStepTimingV1 | null {
  if (!timing) return timing;
  if (index === 0) return timing.mode === 'relative' ? { mode: 'elapsed', elapsedMinutes: timing.delayMinutes } : timing;
  return timing.mode === 'elapsed' && timing.elapsedMinutes === 0 ? { mode: 'relative', delayMinutes: 0 } : timing;
}

/**
 * 指定が前のstepより早く、実際には前のstepの直後にしか送れないかを判定する。
 * 起点が同じelapsed同士でだけ比較する（relativeは前のstep基準、absolute_timeは登録時刻に依存するため
 * 分単位で比較できず、確実に言えるケースだけを対象にする）。
 */
export function isTimingBehindPrevious(steps: readonly { timing: ScenarioStepTimingV1 | null }[], index: number): boolean {
  const timing = steps[index]?.timing;
  if (index === 0 || timing?.mode !== 'elapsed' || timing.elapsedMinutes === null) return false;
  for (let previous = index - 1; previous >= 0; previous -= 1) {
    const earlier = steps[previous].timing;
    if (earlier?.mode !== 'elapsed' || earlier.elapsedMinutes === null) return false;
    if (earlier.elapsedMinutes >= timing.elapsedMinutes) return true;
  }
  return false;
}

/**
 * タイムラインの線上に出す待ち時間ラベル。
 * relativeの第1ステップだけは参加時刻起点なので、登録からの表現に寄せる。
 */
export function timingSummary(timing: ScenarioStepTimingV1 | null, index: number): string {
  if (!timing) return UNSET_TIMING_LABEL;
  if (timing.mode === 'relative') {
    const { delayMinutes } = timing;
    if (delayMinutes === null) return UNSET_TIMING_LABEL;
    if (index === 0) return delayMinutes === 0 ? '登録直後' : `登録から${formatDuration(delayMinutes)}後`;
    return delayMinutes === 0 ? '前のステップ直後' : `前のステップから${formatDuration(delayMinutes)}後`;
  }
  if (timing.mode === 'elapsed') {
    const { elapsedMinutes } = timing;
    if (elapsedMinutes === null) return UNSET_TIMING_LABEL;
    // 2つ目以降は前のステップを待ってから送るため、待機0を「登録直後」と書くと実挙動とずれる
    if (elapsedMinutes === 0) return index === 0 ? '登録直後' : '前のステップ直後';
    return `登録から${formatDuration(elapsedMinutes)}後`;
  }
  const time = formatMinuteOfDay(timing.minuteOfDay);
  if (timing.dayOffset === null || time === null) return UNSET_TIMING_LABEL;
  return timing.dayOffset === 0 ? `登録した日の${time}` : `登録から${timing.dayOffset}日後の${time}`;
}

/** 仕様どおり表示は常にJST固定。環境タイムゾーンに依存させない（AC-0040）。 */
const jstDateTime = new Intl.DateTimeFormat('ja-JP', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Tokyo' }).format;
export const formatJst = (value: string | Date): string => `${jstDateTime(new Date(value))}（日本時間）`;
